// ---------------------------------------------------------------------------
// Analytics dashboard page — key metrics, funnel, volume, providers
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '../components/MetricCard';
import { ConversionFunnel } from '../components/ConversionFunnel';
import { VolumeChart } from '../components/VolumeChart';
import { ProviderPerformanceTable } from '../components/ProviderPerformanceTable';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types (mirrors API response shapes)
// ---------------------------------------------------------------------------

interface DashboardMetrics {
  verificationVolume: number;
  approvalRate: number;
  rejectionRate: number;
  avgProcessingTime: number;
  pendingReviews: number;
  providerErrorRate: number;
  queueDepth: number;
}

interface FunnelStage {
  stage: string;
  count: number;
  dropoffRate: number;
  avgTimeInStage: number;
}

interface ProviderPerformance {
  providerId: string;
  providerName: string;
  successRate: number;
  avgLatency: number;
  errorCount: number;
  lastError: string | null;
}

interface TimeSeriesPoint {
  metric: string;
  value: number;
}

// ---------------------------------------------------------------------------
// Time period options
// ---------------------------------------------------------------------------

type TimePeriod = '7d' | '30d' | '90d';

const PERIOD_KEYS: TimePeriod[] = ['7d', '30d', '90d'];

function periodToDates(period: TimePeriod): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  switch (period) {
    case '7d':
      from.setDate(from.getDate() - 7);
      break;
    case '30d':
      from.setDate(from.getDate() - 30);
      break;
    case '90d':
      from.setDate(from.getDate() - 90);
      break;
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatProcessingTime(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function ChartIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function QueueIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnalyticsDashboardPage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const [period, setPeriod] = useState<TimePeriod>('7d');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [funnel, setFunnel] = useState<FunnelStage[]>([]);
  const [providers, setProviders] = useState<ProviderPerformance[]>([]);
  const [volumeData, setVolumeData] = useState<{ label: string; value: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const periodOptions = useMemo(
    () => PERIOD_KEYS.map((value) => ({ value, label: t(`analyticsDashboard.periods.${value}` as const) })),
    [t],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { from, to } = periodToDates(period);

    try {
      // Fetch all data in parallel using direct fetch
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const params = new URLSearchParams({ from, to, period: 'day' });

      const [metricsRes, funnelRes, providersRes, timeseriesRes] = await Promise.all([
        fetch(`${baseUrl}/v1/admin/analytics/metrics?${params}`).then((r) => r.json()),
        fetch(`${baseUrl}/v1/admin/analytics/funnel?${params}`).then((r) => r.json()),
        fetch(`${baseUrl}/v1/admin/analytics/providers?${params}`).then((r) => r.json()),
        fetch(`${baseUrl}/v1/admin/analytics/timeseries?${params}&metric=verification_volume`).then((r) => r.json()),
      ]);

      setMetrics((metricsRes as { data: DashboardMetrics }).data ?? null);
      setFunnel((funnelRes as { data: FunnelStage[] }).data ?? []);
      setProviders((providersRes as { data: ProviderPerformance[] }).data ?? []);

      const series = (timeseriesRes as { data: TimeSeriesPoint[] }).data ?? [];
      setVolumeData(series.map((p) => ({ label: p.metric, value: p.value })));
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('analyticsDashboard.errors.loadFailed');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [period, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('analyticsDashboard.headings.title')}</h1>
        <div className="flex items-center gap-1 bg-surface-sunken rounded-sm p-1">
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
                period === opt.value
                  ? 'bg-surface-raised text-ink border border-hairline'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Key metric cards */}
      {metrics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard
            title={t('analyticsDashboard.metrics.totalVerifications')}
            value={f.number(metrics.verificationVolume)}
            icon={<ChartIcon />}
          />
          <MetricCard
            title={t('analyticsDashboard.metrics.approvalRate')}
            value={`${metrics.approvalRate.toFixed(1)}%`}
            icon={<CheckIcon />}
            trend={metrics.approvalRate >= 80 ? 'up' : metrics.approvalRate >= 60 ? 'flat' : 'down'}
            trendValue={metrics.approvalRate}
          />
          <MetricCard
            title={t('analyticsDashboard.metrics.avgProcessingTime')}
            value={formatProcessingTime(metrics.avgProcessingTime)}
            icon={<ClockIcon />}
          />
          <MetricCard
            title={t('analyticsDashboard.metrics.pendingReviews')}
            value={f.number(metrics.pendingReviews)}
            icon={<QueueIcon />}
          />
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <VolumeChart data={volumeData} />
        <ConversionFunnel stages={funnel} />
      </div>

      {/* Provider performance */}
      <ProviderPerformanceTable providers={providers} />
    </div>
  );
}
